/*
 * Os caminhos do painel, num lugar só.
 *
 * Tela que monta caminho na mão repete a árvore de `App.tsx` em cada `Link`, e o
 * dia em que uma rota muda vira uma caça por string. Os componentes de rota usam
 * estas funções; os testes também podem, e `App.test.tsx` confere a árvore.
 *
 * Regra do design D2: **entidade no caminho, visão na consulta**. Aba de entidade
 * é segmento, com entrada no histórico, porque debaixo dela abre outra entidade.
 * Aba de lista e filtro ficam na consulta, com `replace`.
 */

import type { FiltrosDaAuditoria, FiltrosDeBoletos, FiltrosDeEntregas } from "@/api/consultas";

export const ROTA_DOS_CEDENTES = "/cedentes";
export const ROTA_DOS_CONVENIOS = `${ROTA_DOS_CEDENTES}?aba=convenios`;
export const ROTA_DAS_APLICACOES = "/aplicacoes";
export const ROTA_DOS_BOLETOS = "/boletos";
export const ROTA_DAS_FATURAS = "/faturas";
export const ROTA_DOS_PLANOS = "/planos";
export const ROTA_DA_OPERACAO = "/operacao";

export type AbaDoCedente = "dados" | "contas" | "aplicacoes" | "boletos";

/**
 * O modal que a rota abre, pela consulta.
 *
 * Ação de pendência é NAVEGAÇÃO (design D2): o caminho mais `?abrir=`. Assim a
 * página do cedente não conhece os modais das abas, e cada aba lê o parâmetro com
 * `useModalNaUrl`. Quem fecha limpa o parâmetro com `replace`.
 *
 * - `editar` abre a edição da entidade do caminho: o cedente na aba Dados, e o
 *   convênio no painel dele.
 * - `certificado` vale nos mesmos dois lugares, e `provisionar` só no convênio.
 * - `conta` é sempre o `conta_id`: o modal herda conta e banco da linha, sem seletor.
 */
export type AberturaDeModal =
  | { abrir: "editar" }
  | { abrir: "certificado" }
  | { abrir: "provisionar" }
  | { abrir: "nova-conta" }
  | { abrir: "editar-conta"; conta: string }
  | { abrir: "novo-convenio"; conta: string }
  | { abrir: "vincular" };

function comAbertura(caminho: string, abertura: AberturaDeModal | undefined): string {
  if (abertura === undefined) return caminho;
  return `${caminho}?${new URLSearchParams(abertura).toString()}`;
}

/**
 * A página do cedente. Sem `aba`, é o caminho que `AbaPadrao` resolve para a
 * primeira aba com pendência.
 */
export function rotaDoCedente(
  id: string,
  aba?: AbaDoCedente,
  abertura?: AberturaDeModal,
): string {
  const base = `${ROTA_DOS_CEDENTES}/${id}`;
  return comAbertura(aba === undefined ? base : `${base}/${aba}`, abertura);
}

/** O painel lateral do convênio, sobre a aba Contas. */
export function rotaDoConvenio(
  pessoaID: string,
  convenioID: string,
  abertura?: AberturaDeModal,
): string {
  return comAbertura(`${ROTA_DOS_CEDENTES}/${pessoaID}/convenios/${convenioID}`, abertura);
}

/** O link de quem só sabe o convênio: a rota descobre o cedente e redireciona. */
export function rotaDoConvenioPorId(convenioID: string): string {
  return `/convenios/${convenioID}`;
}

/** O painel lateral do vínculo, sobre a aba Aplicações. */
export function rotaDoVinculo(pessoaID: string, sistemaID: string): string {
  return `${ROTA_DOS_CEDENTES}/${pessoaID}/aplicacoes/${sistemaID}`;
}

/*
 * A aplicação é página, como o cedente: seis abas com formulário não cabem em 520px
 * (critério do design D1). O cedente da aplicação é painel sobre a aba Cedentes, como o
 * vínculo sobre a aba Aplicações do cedente.
 */
export type AbaDaAplicacao = "contrato" | "cedentes" | "chaves" | "acesso" | "entrega" | "consumo";

/** A aplicação. Sem `aba`, o caminho que a rota resolve para Contrato, com `replace`. */
export function rotaDaAplicacao(id: string, aba?: AbaDaAplicacao): string {
  const base = `${ROTA_DAS_APLICACOES}/${id}`;
  return aba === undefined ? base : `${base}/${aba}`;
}

/** O painel lateral do cedente, sobre a aba Cedentes da aplicação. */
export function rotaDoCedenteDaAplicacao(sistemaID: string, pessoaID: string): string {
  return `${rotaDaAplicacao(sistemaID, "cedentes")}/${pessoaID}`;
}

/*
 * O painel do boleto, da fatura e do plano é rota filha da lista (design D7): a lista
 * segue montada embaixo, com a mesma rolagem. `busca` é a consulta da lista, com o `?`,
 * e é ela que leva os filtros para o fechar devolver.
 */
export function rotaDoBoleto(id: string, busca = ""): string {
  return `${ROTA_DOS_BOLETOS}/${id}${busca}`;
}

export function rotaDaFatura(id: string, busca = ""): string {
  return `${ROTA_DAS_FATURAS}/${id}${busca}`;
}

export function rotaDoPlano(id: string): string {
  return `${ROTA_DOS_PLANOS}/${id}`;
}

function comFiltros(caminho: string, filtros: Record<string, string | undefined>): string {
  const consulta = new URLSearchParams();
  for (const [nome, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== "") consulta.set(nome, valor);
  }
  const texto = consulta.toString();
  return texto === "" ? caminho : `${caminho}?${texto}`;
}

/** A lista de boletos filtrada. Os termos saem codificados: `q` pode trazer `&` e `#`. */
export function rotaDosBoletos(filtros: FiltrosDeBoletos = {}): string {
  return comFiltros(ROTA_DOS_BOLETOS, filtros);
}

/** A lista de convênios filtrada: o destino dos alertas de certificado. */
export function rotaDosConvenios(filtros: { certificado?: string } = {}): string {
  return comFiltros(ROTA_DOS_CEDENTES, { aba: "convenios", ...filtros });
}

export type AbaDaOperacao =
  | "indeterminados"
  | "fila-morta"
  | "divergencias"
  | "titulos-orfaos"
  | "documentos"
  | "provider"
  | "entregas"
  | "auditoria";

/** Aba de lista é visão, e fica na consulta (design D2). */
export function rotaDaOperacao(aba: AbaDaOperacao): string {
  return comFiltros(ROTA_DA_OPERACAO, { aba });
}

/** A aba Entregas, filtrada por boleto ou por aplicação. */
export function rotaDasEntregas(filtros: FiltrosDeEntregas = {}): string {
  return comFiltros(ROTA_DA_OPERACAO, { aba: "entregas", ...filtros });
}

/** A aba Auditoria, filtrada pela entidade: `{ entidade: "boleto", entidade_id }`. */
export function rotaDaAuditoria(filtros: FiltrosDaAuditoria = {}): string {
  return comFiltros(ROTA_DA_OPERACAO, { aba: "auditoria", ...filtros });
}

/*
 * De que lista o operador veio.
 *
 * A linha da tabela guarda no `state` da navegação o caminho com os filtros, e o
 * "« voltar" da página volta para ela, e não para a lista crua: filtrar por banco,
 * abrir um cedente e perder o filtro ao voltar é o tipo de atrito que faz a tela
 * parecer estranha sem que ninguém aponte o porquê. O `state` acompanha F5, e link
 * colado não o tem: aí vale a lista inteira.
 */
export function estadoComOrigem(local: { pathname: string; search: string }) {
  return { origem: `${local.pathname}${local.search}` };
}

export function origemDoEstado(estado: unknown): string | undefined {
  if (typeof estado !== "object" || estado === null || !("origem" in estado)) return undefined;
  return typeof estado.origem === "string" ? estado.origem : undefined;
}
